import { useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import type { CustomFont } from "@/react/components/shared/BuilderUI/Store/types";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

export const googleFontQueryKeyGenerator = (pageId: number) => [
  "custom-fonts",
  "google-fonts",
  pageId,
];

export interface CustomFontOption {
  createdAt: string;
  fallbackFont: string;
  family: string;
  fontType: "google" | "custom" | "web-font";
  id: number;
  variants: string[];
}

export const useGetGoogleFonts = (pageId: number) => {
  const toast = useToast();

  return useQuery<CustomFont[], ApiError, CustomFontOption[]>({
    queryKey: googleFontQueryKeyGenerator(pageId),
    queryFn: () =>
      reactQueryGet(
        internalApi.settings.sites.pages.custom_fonts.index({ pageId }),
      ),
    select: (data: CustomFont[]) =>
      data
        .filter(font => font.font_type === "google")
        .map(font => ({
          id: font.id,
          family: font.font_family,
          variants: font.variants.split(",").map(v => v.trim()),
          fontType: font.font_type,
          fallbackFont: font.fallback_font,
          createdAt: font.created_at,
        }))
        .sort((a, b) => a.family.localeCompare(b.family)),
    staleTime: 1000 * 60 * 5,
    onError: () => {
      toast.error(t("something_went_wrong"));
    },
  });
};
